(() => {
  if (customElements.get('offbits-cart-timer')) return;

  const assignmentKey = 'offbits_cart_timer_assignment_v1';
  const deadlinePrefix = 'offbits_cart_timer_deadline_v1:';
  const trackedViews = new Set();
  const trackedExpiries = new Set();

  const pushEvent = (event, detail = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...detail });
  };

  const readSession = (key) => {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  };

  const writeSession = (key, value) => {
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      // The timer still works for the current page when storage is unavailable.
    }
  };

  const getAssignment = (percentage) => {
    let bucket = Number(readSession(assignmentKey));
    if (!Number.isFinite(bucket) || bucket < 1 || bucket > 100) {
      bucket = Math.floor(Math.random() * 100) + 1;
      writeSession(assignmentKey, String(bucket));
    }

    return {
      bucket,
      variant: bucket <= percentage ? 'timer' : 'control'
    };
  };

  class OffbitsCartTimer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.offbitsInitialized === 'true') return;
      this.dataset.offbitsInitialized = 'true';

      this.context = this.dataset.context || 'cart';
      this.durationMinutes = Math.max(1, Number(this.dataset.durationMinutes) || 10);
      this.audiencePercentage = Math.min(100, Math.max(0, Number(this.dataset.audiencePercentage) || 0));
      this.signature = this.dataset.cartSignature || 'cart';
      this.messageElement = this.querySelector('[data-offbits-timer-message]');
      this.valueElement = this.querySelector('[data-offbits-timer-value]');

      const assignment = getAssignment(this.audiencePercentage);
      this.dataset.experimentVariant = assignment.variant;
      this.dataset.experimentBucket = String(assignment.bucket);

      if (!window.__offbitsCartTimerAssignmentTracked) {
        window.__offbitsCartTimerAssignmentTracked = true;
        pushEvent('offbits_cart_timer_assignment', {
          cart_timer_variant: assignment.variant,
          cart_timer_bucket: assignment.bucket,
          cart_timer_audience_percentage: this.audiencePercentage
        });
      }

      if (assignment.variant !== 'timer') {
        this.hidden = true;
        return;
      }

      this.hidden = false;
      this.startCountdown();
      this.observeVisibility();
    }

    disconnectedCallback() {
      if (this.intervalId) clearInterval(this.intervalId);
      if (this.visibilityObserver) this.visibilityObserver.disconnect();
    }

    startCountdown() {
      const storageKey = deadlinePrefix + this.durationMinutes + ':' + this.signature;
      let deadline = Number(readSession(storageKey));

      if (!Number.isFinite(deadline) || deadline <= 0) {
        deadline = Date.now() + this.durationMinutes * 60 * 1000;
        writeSession(storageKey, String(deadline));
      }

      this.deadline = deadline;
      this.tick();
      if (deadline > Date.now()) {
        this.intervalId = window.setInterval(() => this.tick(), 1000);
      }
    }

    tick() {
      const remainingMs = Math.max(0, this.deadline - Date.now());
      const remainingSeconds = Math.ceil(remainingMs / 1000);
      this.dataset.remainingSeconds = String(remainingSeconds);

      if (remainingSeconds <= 0) {
        this.expire();
        return;
      }

      const minutes = Math.floor(remainingSeconds / 60);
      const seconds = remainingSeconds % 60;
      if (this.valueElement) {
        this.valueElement.hidden = false;
        this.valueElement.textContent = String(minutes).padStart(2, '0') + ':' + String(seconds).padStart(2, '0');
      }
    }

    expire() {
      if (this.intervalId) clearInterval(this.intervalId);
      this.intervalId = null;
      this.dataset.expired = 'true';

      if (this.messageElement) {
        this.messageElement.textContent = this.dataset.expiredMessage || 'Your cart is still available — checkout when ready';
      }
      if (this.valueElement) this.valueElement.hidden = true;

      const expiryKey = this.context + ':' + this.signature;
      if (!trackedExpiries.has(expiryKey)) {
        trackedExpiries.add(expiryKey);
        pushEvent('offbits_cart_timer_expired', {
          cart_timer_context: this.context,
          cart_timer_duration_minutes: this.durationMinutes,
          cart_signature: this.signature
        });
      }
    }

    observeVisibility() {
      const trackView = () => {
        const viewKey = this.context + ':' + this.signature;
        if (trackedViews.has(viewKey)) return;
        trackedViews.add(viewKey);
        pushEvent('offbits_cart_timer_view', {
          cart_timer_context: this.context,
          cart_timer_duration_minutes: this.durationMinutes,
          cart_timer_audience_percentage: this.audiencePercentage,
          cart_signature: this.signature
        });
      };

      if (!('IntersectionObserver' in window)) {
        trackView();
        return;
      }

      this.visibilityObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          trackView();
          this.visibilityObserver.disconnect();
        }
      }, { threshold: 0.25 });

      this.visibilityObserver.observe(this);
    }
  }

  customElements.define('offbits-cart-timer', OffbitsCartTimer);

  document.addEventListener('click', (event) => {
    const checkout = event.target.closest('#checkout, #CartDrawer-Checkout, button[name="checkout"]');
    if (!checkout) return;

    const context = checkout.id === 'CartDrawer-Checkout' ? 'cart-drawer' : 'cart-page';
    const timer = Array.from(document.querySelectorAll('offbits-cart-timer:not([hidden])'))
      .find((element) => element.dataset.context === context);

    if (!timer) return;

    pushEvent('offbits_cart_timer_checkout_click', {
      cart_timer_context: context,
      cart_timer_remaining_seconds: Number(timer.dataset.remainingSeconds || 0),
      cart_timer_expired: timer.dataset.expired === 'true',
      cart_signature: timer.dataset.cartSignature || ''
    });
  });
})();
